/**
 * 356: the platform settings as the admin reads them (`GET /platform-config`) and the "patch shape"
 * a save sends (contracts/settings.md). The patch shape holds only what staff can set.
 */
export type OperatingHoursPrecedence = 'car' | 'location' | 'intersection';
export type Mode = 'off' | 'count' | 'on';

export interface Fee {
  enabled: boolean;
  amount_minor: number;
  taxable: boolean;
  refundable: boolean;
}
export interface Sst {
  enabled: boolean;
  rate_bps: number;
}
export interface ChannelConfig {
  enabled: boolean;
  fee: Fee;
  disabled_by: 'staff' | 'system' | null;
  disabled_at: string | null;
  failure_count: number;
  failure_window_started_at: string | null;
}
export interface ExtensionPolicy {
  min_notice_minutes: number;
  max_total_extension_minutes: number | null;
}
export interface BookingTimers {
  hold_minutes: number;
  unpaid_cancel_mode: Mode;
  unpaid_cancel_minutes: number;
}
/** 374: when customers may send their pickup and return photos, in minutes around the rental's times. */
export interface InspectionWindows {
  pickup_before_minutes: number;
  pickup_after_minutes: number;
  return_before_minutes: number;
  return_after_minutes: number;
}
export interface AutoWorkflow {
  mode: Mode;
  activated_at: string | null;
}

/** 389 (#1305): one poster of the home pop-up as the platform serves it to the card. */
export interface HomePopupPoster {
  id: string;
  picture_id: string;
  button_label: string | null;
  link: string | null;
  starts_at: string | null;
  ends_at: string | null;
  image_url: string | null;
}

/** 389: a poster as a save sends it (`id` absent for a new poster). */
export interface HomePopupPosterPatch {
  id?: string;
  picture_id: string;
  button_label: string | null;
  link: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

/** 395 (#1336): how often a customer's device shows the pop-up. */
export type HomePopupFrequency = 'every_visit' | 'daily' | 'per_change';

export interface HomePopupPatch {
  enabled: boolean;
  posters: HomePopupPosterPatch[];
  /** 395: always sent by the card (the platform serves every visit when never chosen). */
  frequency: HomePopupFrequency;
}

/** 390 (#1310): the business details printed at the top of every customer receipt. */
export interface ReceiptDetails {
  company_name: string;
  registration_no: string;
  address: string;
  sst_no: string;
}

export interface PlatformConfig {
  operating_hours_precedence: OperatingHoursPrecedence;
  platform_fee?: Fee;
  sst?: Sst;
  channel_configs?: Record<string, ChannelConfig>;
  extension_policy?: ExtensionPolicy;
  booking_timers?: BookingTimers;
  customer_inspection_photos?: boolean;
  inspection_windows?: InspectionWindows;
  auto_workflow?: AutoWorkflow;
  /** 386: the gap between rentals, in minutes (the platform serves 30 when never saved). */
  booking_buffer_minutes?: number;
  /** 389: the home pop-up (the platform serves off, no posters, when never saved). */
  home_popup?: {
    enabled: boolean;
    version: string | null;
    posters: HomePopupPoster[];
    frequency?: HomePopupFrequency;
  };
  /** 390: the receipt's issuer (the platform serves empty strings when never saved). */
  receipt_details?: ReceiptDetails;
  /** 392: the booking email switches (the platform fills every key, defaults for unset). */
  booking_emails?: BookingEmails;
  /**
   * 393: the penalty payment QR, with a 10-minute link for the card (none when never saved).
   * 409: CARii's commission on every penalty, in basis points (the platform serves 2500 when never saved).
   */
  penalty_payment?: {
    qr_picture_id: string | null;
    qr_url?: string | null;
    commission_rate_bps?: number;
  };
  /** 417: the mileage package terms (the platform serves its defaults when never saved). */
  mileage_packages?: MileagePackages;
  created_at: string;
  updated_at: string;
}

/** 417 (#1464): the mileage package terms, in sen and km. */
export interface MileagePackages {
  package1_price_minor: number;
  package2_price_minor: number;
  package2_allowance_km: number;
  rate_minor_per_km: number;
}

/** 409 (#1471): CARii's share of a penalty when the platform rate was never saved (25 %), as the platform reads it. */
export const PENALTY_COMMISSION_DEFAULT_BPS = 2500;

/** 392 (#1330): the booking events whose email can be switched off. In-app and push always go. */
export const BOOKING_EMAIL_EVENTS = [
  'confirmed',
  'cancelled',
  'awaiting_payment',
  'pickup_reminder',
  'pickup_soon',
  'return_reminder',
  'return_soon',
  'status_update',
  'changed',
  // 393 (#1313)
  'penalty_recorded',
  // 407 (#1370)
  'return_overdue',
] as const;
export type BookingEmailEvent = (typeof BOOKING_EMAIL_EVENTS)[number];
export type BookingEmails = Record<BookingEmailEvent, boolean>;

/** A channel as a save sets it. */
export interface ChannelPatch {
  enabled: boolean;
  fee: Fee;
}

export interface SettingsValues {
  operating_hours_precedence: OperatingHoursPrecedence;
  platform_fee: Fee | null;
  sst: Sst | null;
  channel_configs: Record<string, ChannelPatch> | null;
  extension_policy: ExtensionPolicy | null;
  booking_timers: BookingTimers | null;
  customer_inspection_photos: boolean;
  inspection_windows: InspectionWindows | null;
  auto_workflow: { mode: Mode } | null;
  booking_buffer_minutes: number | null;
  home_popup: HomePopupPatch | null;
  receipt_details: ReceiptDetails | null;
  booking_emails: BookingEmails | null;
  /** 409: `commission_rate_bps` is null while the field is empty (the platform refuses it; never saved as 0 %). */
  penalty_payment: { qr_picture_id: string | null; commission_rate_bps: number | null } | null;
  /** 417: a field is null while it is empty (the platform refuses it; never saved as 0). */
  mileage_packages: { [K in keyof MileagePackages]: number | null } | null;
}

export type ConfigKey = keyof SettingsValues;

export type TabId =
  | 'money'
  | 'booking-rules'
  | 'automation'
  | 'legal'
  | 'app'
  | 'notifications'
  | 'history';

export const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'money', label: 'Money' },
  { id: 'booking-rules', label: 'Booking rules' },
  { id: 'automation', label: 'Automation' },
  { id: 'legal', label: 'Legal' },
  // 389: what customers see in the app.
  { id: 'app', label: 'App' },
  // 392: which booking events also email the customer.
  { id: 'notifications', label: 'Notifications' },
  { id: 'history', label: 'History' },
];

/** The PATCH keys each tab saves. Legal and History save nothing through PATCH. */
export const TAB_KEYS: Record<TabId, ConfigKey[]> = {
  money: ['platform_fee', 'sst', 'channel_configs', 'mileage_packages'],
  'booking-rules': [
    'operating_hours_precedence',
    'extension_policy',
    'customer_inspection_photos',
    'inspection_windows',
    'booking_buffer_minutes',
  ],
  automation: ['booking_timers', 'auto_workflow'],
  legal: [],
  app: ['home_popup', 'receipt_details', 'penalty_payment'],
  notifications: ['booking_emails'],
  history: [],
};

export function isTabId(v: string | null): v is TabId {
  return TABS.some((t) => t.id === v);
}

/** The values the page edits, in the patch shape, from what the platform served. */
export function toValues(config: PlatformConfig): SettingsValues {
  const channels = config.channel_configs;
  return {
    operating_hours_precedence: config.operating_hours_precedence,
    platform_fee: config.platform_fee ? { ...config.platform_fee } : null,
    sst: config.sst ? { ...config.sst } : null,
    channel_configs: channels
      ? Object.fromEntries(
          Object.entries(channels).map(([id, c]) => [
            id,
            { enabled: c.enabled, fee: { ...c.fee } },
          ]),
        )
      : null,
    extension_policy: config.extension_policy ? { ...config.extension_policy } : null,
    booking_timers: config.booking_timers ? { ...config.booking_timers } : null,
    customer_inspection_photos: config.customer_inspection_photos !== false,
    inspection_windows: config.inspection_windows ? { ...config.inspection_windows } : null,
    auto_workflow: config.auto_workflow ? { mode: config.auto_workflow.mode } : null,
    booking_buffer_minutes: config.booking_buffer_minutes ?? null,
    home_popup: config.home_popup
      ? {
          enabled: config.home_popup.enabled,
          posters: config.home_popup.posters.map((p) => ({
            id: p.id,
            picture_id: p.picture_id,
            button_label: p.button_label,
            link: p.link,
            starts_at: p.starts_at,
            ends_at: p.ends_at,
          })),
          frequency: config.home_popup.frequency ?? 'every_visit',
        }
      : null,
    receipt_details: config.receipt_details ? { ...config.receipt_details } : null,
    booking_emails: config.booking_emails ? { ...config.booking_emails } : null,
    // 393: only the id is saved; the link is for showing. 409: the rate beside it.
    penalty_payment: config.penalty_payment
      ? {
          qr_picture_id: config.penalty_payment.qr_picture_id ?? null,
          commission_rate_bps:
            config.penalty_payment.commission_rate_bps ?? PENALTY_COMMISSION_DEFAULT_BPS,
        }
      : null,
    mileage_packages: config.mileage_packages ? { ...config.mileage_packages } : null,
  };
}

export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    sameValue((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}
